import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/hooks/useTheme';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import TaskListView from '@/pages/TaskListView';
import RewardsView from '@/pages/RewardsView';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import DeleteAccount from '@/pages/DeleteAccount';
import ProtectedRoute from '@/components/ProtectedRoute';
import InstallPrompt from '@/components/InstallPrompt';
import FeedbackButton from '@/components/FeedbackButton';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/list/:listId"
        element={
          <ProtectedRoute>
            <TaskListView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards/:listId"
        element={
          <ProtectedRoute>
            <RewardsView />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // Setup service worker message listener
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        console.log('[App] Message from SW:', event.data);
        
        // Handle sync complete
        if (event.data?.type === 'SYNC_COMPLETE') {
          console.log('[App] Sync completed for:', event.data.entity);
          // Could trigger a refresh of data here
        }
        
        // Handle data refresh
        if (event.data?.type === 'DATA_REFRESHED') {
          console.log('[App] Data refreshed in background');
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      
      // Register periodic sync if supported
      const registerPeriodicSync = async () => {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          // Check if periodic sync is supported
          if ('periodicSync' in registration) {
            const status = await navigator.permissions.query({
              name: 'periodic-background-sync',
            });
            
            if (status.state === 'granted') {
              await registration.periodicSync.register('refresh-data', {
                minInterval: 60 * 60 * 1000, // 1 hour
              });
              console.log('[App] Periodic sync registered');
            }
          }
        } catch (error) {
          console.log('[App] Periodic sync not supported:', error);
        }
      };

      registerPeriodicSync();

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] transition-colors">
        <BrowserRouter>
          <AppRouter />
          <InstallPrompt />
          <FeedbackButton />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
